import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Mail, KeyRound, MailCheck } from 'lucide-react';
import AuthShell, { authInputCls, authButtonCls } from '../components/auth/AuthShell';
import { api, ApiError } from '../lib/api';

// /forgot-password — request a reset link by email. The answer is the same
// whether or not the address has an account, so nobody can use this page to
// find out who has one.
export default function ForgotPassword() {
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || '');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState('');
  const [error, setError] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Enter the email address you sign in with.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/api/auth/forgot-password', { email: email.trim() });
      setDone(res?.message || 'If that email has a NexSav portal account, we sent it a link to reset the password.');
    } catch (err) {
      if (err instanceof ApiError) setError(err.message || 'Could not send the link.');
      else setError('Network error, please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <AuthShell badge="Check your email" badgeIcon={MailCheck} title="Check your inbox" subtitle={done}>
        <div className="flex flex-col gap-3 text-center">
          <p className="text-[12px] text-gray-500">Nothing after a few minutes? Check spam or junk, then try again.</p>
          <button type="button" onClick={() => setDone('')} className="text-sm text-gray-400 hover:text-white transition-colors">Try a different email</button>
          <Link to="/login" className="text-sm text-gray-400 hover:text-white transition-colors">Back to sign in</Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell badge="Reset password" badgeIcon={KeyRound} title="Forgot your password?" subtitle="Enter your email and we’ll send you a link to choose a new one.">
      <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
        <div>
          <label htmlFor="fp-email" className="block text-xs font-medium text-gray-400/80 mb-2 tracking-wide">Email</label>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600 pointer-events-none" aria-hidden="true" />
            <input
              id="fp-email"
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@school.org"
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? 'fp-error' : undefined}
              className={authInputCls}
            />
          </div>
        </div>
        {error && (
          <div id="fp-error" className="px-4 py-3 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-sm text-red-300/90" role="alert">{error}</div>
        )}
        <button type="submit" disabled={submitting} className={authButtonCls}>
          {submitting ? 'Sending…' : 'Send reset link'}
        </button>
      </form>
    </AuthShell>
  );
}
